import React, { useEffect, useState } from 'react';
import StaticKeyMetricCard from './StaticKeyMetricCard';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import { ContentMetricType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';

interface ContentKeyMetricCardProps {
  variant: 'large' | 'small' | 'medium';
  indexName: string;
  fieldName: string;
  title?: string;
  onClick?: () => void;
}

const ContentKeyMetricCard = ({
  variant,
  indexName,
  fieldName,
  title,
  onClick
}: ContentKeyMetricCardProps) => {
  const { getContentScoresByWeek } = useContentService();
  const { mainTimePeriod, comparisonTimePeriod } = useAppSelector(state => state);

  const [contentMetricsData, setContentMetricsData] = useState<{
    primaryData: number[][];
    secondaryData: number[][];
    formattedPrimaryTotal: string;
    formattedPercentChange: string;
    percentChange: number;
    isLoading: boolean;
  }>({
    primaryData: [],
    secondaryData: [],
    formattedPrimaryTotal: '0%',
    formattedPercentChange: '0%',
    percentChange: 0,
    isLoading: true
  });

  useEffect(() => {
    const fetchData = async () => {
      setContentMetricsData(prev => ({ ...prev, isLoading: true }));
      
      try {
        const contentScores = await getContentScoresByWeek(
          {
            contentMetricType: ContentMetricType.ContentUtilizationScore,
            mainTimePeriod,
            comparisonTimePeriod
          }
        );
        
        const { primaryMetrics, secondaryMetrics } = contentScores.chartData;
        
        const mostRecentPrimaryData = primaryMetrics.length > 0 ? 
          primaryMetrics[primaryMetrics.length - 1][1] : 0;
        
        const mostRecentSecondaryData = secondaryMetrics.length > 0 ? 
          secondaryMetrics[secondaryMetrics.length - 1][1] : 0;
        
        const calculatedPercentChange = mostRecentSecondaryData !== 0 ?
          ((mostRecentPrimaryData - mostRecentSecondaryData) / mostRecentSecondaryData) * 100 : 0;
        
        setContentMetricsData({
          primaryData: primaryMetrics,
          secondaryData: secondaryMetrics,
          formattedPrimaryTotal: `${(mostRecentPrimaryData * 100).toFixed(2)}%`,
          formattedPercentChange: `${Math.abs(Math.round(calculatedPercentChange * 100) / 100).toFixed(2)}%`,
          percentChange: calculatedPercentChange,
          isLoading: false
        });
      } catch (error) {
        console.error('Error fetching content metrics data:', error);
        setContentMetricsData(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTimePeriod, comparisonTimePeriod]);

  return (
    <StaticKeyMetricCard
      variant={variant}
      indexName={indexName}
      fieldName={fieldName}
      title={title || "Content"}
      onClick={onClick}
      isLoading={contentMetricsData.isLoading}
      primaryData={contentMetricsData.primaryData}
      secondaryData={contentMetricsData.secondaryData}
      formattedPrimaryTotal={contentMetricsData.formattedPrimaryTotal}
      formattedPercentChange={contentMetricsData.formattedPercentChange}
      percentChange={contentMetricsData.percentChange}
    />
  );
};

export default ContentKeyMetricCard; 